// helpers/finishDealerLogin.js
import { auth } from "@/config/firebaseConfig";
import { ensurePersistence } from "@/app/helpers/persistence";
import {
  isDealerLogoutInProgress,
  subscribeToDealerLogout,
} from "@/helpers/dealerAuthSession";

let sessionCookiePromise = null;
let sessionCookieUid = null;

const clearDealerApiSession = async (api) => {
  try {
    await fetch(`${api}/auction/logout`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      keepalive: true,
    });
  } catch {}
};

export default function finishDealerLogin(api, router) {
  if (isDealerLogoutInProgress()) {
    return Promise.reject(new Error("Dealer logout is in progress"));
  }

  const requestedUid = auth.currentUser?.uid || null;
  if (
    sessionCookiePromise &&
    requestedUid &&
    sessionCookieUid &&
    requestedUid !== sessionCookieUid
  ) {
    return sessionCookiePromise
      .catch(() => {})
      .then(() => finishDealerLogin(api, router));
  }

  if (!sessionCookiePromise) {
    sessionCookieUid = requestedUid;
    sessionCookiePromise = (async () => {
      const controller = new AbortController();
      const unsubscribeLogout = subscribeToDealerLogout(() => {
        controller.abort();
      });
      let ownerUid = requestedUid;
      let sessionResponseReceived = false;

      try {
        await ensurePersistence(auth);
        if (isDealerLogoutInProgress()) {
          throw new Error("Dealer logout is in progress");
        }

        ownerUid = auth.currentUser?.uid || null;
        sessionCookieUid = ownerUid;
        const idToken = await auth.currentUser?.getIdToken(false);
        if (!idToken) throw new Error("No current user");
        if (isDealerLogoutInProgress()) {
          throw new Error("Dealer logout is in progress");
        }
        if (auth.currentUser?.uid !== ownerUid) {
          throw new Error("Dealer auth changed while creating session cookie");
        }

        const res = await fetch(`${api}/auction/session-cookie`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // setzt __session_dealer_api (HttpOnly)
          cache: "no-store",
          signal: controller.signal,
          body: JSON.stringify({ idToken }),
        });
        sessionResponseReceived = true;
        if (!res.ok) {
          const responseText = await res.text();
          console.error("[finishDealerLogin] backend error:", responseText);
          throw new Error(
            `Dealer session could not be created (${res.status})`
          );
        }
        const { sessionCookie } = await res.json();
        if (!sessionCookie) {
          throw new Error(
            "Dealer session response did not include a session cookie"
          );
        }

        if (
          isDealerLogoutInProgress() ||
          auth.currentUser?.uid !== ownerUid
        ) {
          throw new Error("Dealer auth changed while creating session cookie");
        }

        const prod = process.env.NODE_ENV === "production";
        // Front-Cookie für SSR (gleiches JWT, nur lesbar für Next SSR):
        document.cookie = `__session_dealer_front=${sessionCookie}; Path=/; SameSite=Lax; Max-Age=${
          14 * 24 * 60 * 60
        }${prod ? "; Secure" : ""}`;

        // (optional) URL säubern
        const cleanUrl = window.location.pathname + window.location.hash;
        if (router) router.replace(cleanUrl);
      } catch (error) {
        if (
          isDealerLogoutInProgress() ||
          (sessionResponseReceived && auth.currentUser?.uid !== ownerUid)
        ) {
          await clearDealerApiSession(api);
        }
        throw error;
      } finally {
        unsubscribeLogout();
      }
    })().finally(() => {
      sessionCookiePromise = null;
      sessionCookieUid = null;
    });
  }

  return sessionCookiePromise;
}
